import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollAreaScrollbar, ScrollAreaViewport } from '@/components/ui/scroll-area';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { Search, X, FolderOpen, PoundSterling, CheckSquare, Square } from 'lucide-react';

interface ProjectsFiltersProps {
  filters: TimesheetReportFilters;
  onFiltersChange: (filters: TimesheetReportFilters) => void;
  availableProjects: Array<{ id: string; code: string; name: string }>;
  availableCostCentres: Array<{ id: string; code: string; name: string }>;
}

export const ProjectsFilters: React.FC<ProjectsFiltersProps> = ({
  filters,
  onFiltersChange,
  availableProjects,
  availableCostCentres,
}) => {
  const [projectSearch, setProjectSearch] = useState('');
  const [costCentreSearch, setCostCentreSearch] = useState('');

  // Filter projects based on search
  const filteredProjects = useMemo(() => {
    if (!projectSearch) return availableProjects;
    const searchLower = projectSearch.toLowerCase();
    return availableProjects.filter(
      (proj) =>
        proj.code.toLowerCase().includes(searchLower) ||
        proj.name.toLowerCase().includes(searchLower)
    );
  }, [availableProjects, projectSearch]);

  // Filter cost centres based on search
  const filteredCostCentres = useMemo(() => {
    if (!costCentreSearch) return availableCostCentres;
    const searchLower = costCentreSearch.toLowerCase();
    return availableCostCentres.filter(
      (cc) =>
        cc.code.toLowerCase().includes(searchLower) ||
        cc.name.toLowerCase().includes(searchLower)
    );
  }, [availableCostCentres, costCentreSearch]);

  // Handle project selection
  const handleProjectToggle = (projectId: string) => {
    const currentProjects = filters.projectIds || [];
    const newProjects = currentProjects.includes(projectId)
      ? currentProjects.filter((id) => id !== projectId)
      : [...currentProjects, projectId];

    onFiltersChange({
      ...filters,
      projectIds: newProjects.length > 0 ? newProjects : undefined,
    });
  };

  // Bulk selection handlers for projects
  const handleSelectAllProjects = () => {
    onFiltersChange({
      ...filters,
      projectIds: availableProjects.map((proj) => proj.id),
    });
  };

  const handleClearAllProjects = () => {
    onFiltersChange({
      ...filters,
      projectIds: undefined,
    });
    setProjectSearch('');
  };

  const selectedProjectCount = filters.projectIds?.length || 0;

  return (
    <div className="space-y-6">
      {/* Projects Filter */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <FolderOpen className="h-5 w-5" />
                Projects
              </CardTitle>
              <CardDescription>
                Filter by specific projects
                {selectedProjectCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedProjectCount} selected
                  </Badge>
                )}
              </CardDescription>
            </div>
            {selectedProjectCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAllProjects}
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects by code or name..."
              value={projectSearch}
              onChange={(e) => setProjectSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Bulk Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAllProjects}
              className="flex-1 gap-2"
            >
              <CheckSquare className="h-4 w-4" />
              Select All ({availableProjects.length})
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAllProjects}
              className="flex-1 gap-2"
              disabled={selectedProjectCount === 0}
            >
              <Square className="h-4 w-4" />
              Clear Selection
            </Button>
          </div>

          {/* Project List */}
          <ScrollArea className="h-[300px] max-h-[40vh] border rounded-md">
            <div className="p-4 space-y-2">
              {filteredProjects.length > 0 ? (
                filteredProjects.map((proj) => {
                  const isChecked = filters.projectIds?.includes(proj.id) || false;

                  return (
                    <div key={proj.id} className="flex items-start gap-2 p-2 hover:bg-muted rounded-md">
                      <Checkbox
                        id={`project-${proj.id}`}
                        checked={isChecked}
                        onCheckedChange={() => handleProjectToggle(proj.id)}
                        className="mt-1"
                      />
                      <Label
                        htmlFor={`project-${proj.id}`}
                        className="text-sm font-normal cursor-pointer flex-1"
                      >
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono text-xs">
                            {proj.code}
                          </Badge>
                          <span>{proj.name}</span>
                        </div>
                      </Label>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No projects found matching "{projectSearch}"
                </p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Cost Centres Filter - Coming Soon */}
      <Card className="opacity-60">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <PoundSterling className="h-5 w-5" />
                Cost Centres
                <Badge variant="outline" className="ml-2">Coming Soon</Badge>
              </CardTitle>
              <CardDescription>
                Filter by cost centres (Phase 2 feature)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search cost centres..."
              value={costCentreSearch}
              onChange={(e) => setCostCentreSearch(e.target.value)}
              className="pl-9"
              disabled
            />
          </div>

          {/* Placeholder */}
          <div className="h-[200px] border rounded-md flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <PoundSterling className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p className="text-sm">Cost centre filtering will be available in Phase 2</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950/20 p-4 rounded-md border border-blue-200 dark:border-blue-900">
        <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">💡 Tip</p>
        <p className="text-blue-800 dark:text-blue-200">
          Project filters help you analyze time spent on specific initiatives. Use the search
          function to quickly find projects by code or name.
        </p>
      </div>
    </div>
  );
};
